import { create } from 'zustand';
import axios from 'axios';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import { createBookJaneRequestHeaders } from 'utils/createBookJaneRequest';
import { DASHBOARDS_CHECK, DASHBOARDS_LAST_REFRESH } from 'constants/endpoints';

interface LastRefreshResponse {
  last_refresh: string | null;
}

const DATA_LOCKED_STATUS = 423;

export const DATA_LOCK_POLL_INTERVAL_MS = 5 * 60 * 1000;

const healthClient = axios.create();

async function fetchCheckStatus(): Promise<number> {
  const response = await healthClient.get(DASHBOARDS_CHECK(), {
    headers: createBookJaneRequestHeaders(),
    validateStatus: (status) => status === 200 || status === 503 || status === DATA_LOCKED_STATUS,
  });
  return response.status;
}

async function fetchLastRefresh(): Promise<string | null> {
  const response = await healthClient.get<LastRefreshResponse>(DASHBOARDS_LAST_REFRESH(), {
    headers: createBookJaneRequestHeaders(),
  });
  return response.data?.last_refresh ?? null;
}

interface ReportsDataHealthState {
  isRefreshing: boolean;
  lastRefreshAt: string | null;
  hasChecked: boolean;
  isDataLocked: boolean;
  dataVersion: number;
}

interface ReportsDataHealthActions {
  fetchHealth: () => Promise<void>;
  markDataLocked: () => void;
  checkDataLockRecovery: () => Promise<void>;
  reset: () => void;
}

const INITIAL_STATE: ReportsDataHealthState = {
  isRefreshing: false,
  lastRefreshAt: null,
  hasChecked: false,
  isDataLocked: false,
  dataVersion: 0,
};

let pollTimer: ReturnType<typeof setInterval> | null = null;
let recoveryCheckInFlight = false;

function stopDataLockPoll(): void {
  if (pollTimer === null) return;
  clearInterval(pollTimer);
  pollTimer = null;
}

export const reportsDataHealthStore = create<ReportsDataHealthState & ReportsDataHealthActions>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,
    fetchHealth: () => {
      return Promise.allSettled([fetchCheckStatus(), fetchLastRefresh()]).then(
        ([checkResult, lastRefreshResult]) => {
          set({
            isRefreshing: checkResult.status === 'fulfilled' && checkResult.value === 503,
            lastRefreshAt:
              lastRefreshResult.status === 'fulfilled' ? lastRefreshResult.value : null,
            hasChecked: true,
          });
        },
      );
    },
    markDataLocked: () => {
      if (get().isDataLocked && pollTimer !== null) return;
      stopDataLockPoll();
      set({ isDataLocked: true });
      pollTimer = setInterval(() => {
        void get().checkDataLockRecovery();
      }, DATA_LOCK_POLL_INTERVAL_MS);
    },
    checkDataLockRecovery: () => {
      if (!get().isDataLocked || recoveryCheckInFlight) return Promise.resolve();
      recoveryCheckInFlight = true;
      return fetchCheckStatus()
        .then((status) => {
          if (status !== 200) return;
          stopDataLockPoll();
          set((state) => ({ isDataLocked: false, dataVersion: state.dataVersion + 1 }));
          return get().fetchHealth();
        })
        .catch(() => undefined)
        .finally(() => {
          recoveryCheckInFlight = false;
        });
    },
    reset: () => {
      stopDataLockPoll();
      recoveryCheckInFlight = false;
      set(INITIAL_STATE);
    },
  })),
);

const getResponseStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

export function isDataLockError(error: unknown): boolean {
  return getResponseStatus(error) === DATA_LOCKED_STATUS;
}

export function hasDataLockRejection(results: PromiseSettledResult<unknown>[]): boolean {
  return results.some((result) => result.status === 'rejected' && isDataLockError(result.reason));
}

export const useReportsDataHealthStore = reportsDataHealthStore;
